// ==============================|| EFFORT TYPES ||============================== //

import { ContactMinDto } from './contacts';
import { ExpenseDto, ExpenseFormData } from './expenses';
import { MinDto, NamedEntityMinDto } from './other';

export interface EffortListItem {
    id: number;
    effortActivityTypeCode: string;
    description?: string | null;
    effortDate: string;
    usedTimeInSeconds: number;
    effortsByEmployees: NamedEntityMinDto[];
    effortsForPeople: ContactMinDto[];
    effortsForCompanies: ContactMinDto[];
    /** A draft may have no activity type, date or time yet. */
    isDraft?: boolean;
}

export interface EffortExportListItemDto {
    id: number;
    effortDate: string;
    usedTimeInSeconds: number;
    effortActivityTypeCode: string;
    description?: string | null;
    effortsByEmployees: NamedEntityMinDto[];
    effortsForPeople: ContactMinDto[];
    effortsForCompanies: ContactMinDto[];
    effortsResultingInCases: SimplifiedMatterDto[];
}

export interface Effort {
    id: number;
    effortActivityTypeCode?: string;
    description?: string;
    effortDate?: string;
    usedTimeInSeconds?: number;
    effortByEmployee?: NamedEntityMinDto;
    employees?: EffortEmployee[];
    parties?: EffortParty[];
    matters?: EffortMatter[];
    expenses?: ExpenseDto[];
    isEditable?: boolean;
    isDeletable?: boolean;
    isDraft?: boolean;
    // Audit metadata. createdBy/lastModifiedBy are the raw cognito ids; createdByEmployee/
    // changedByEmployee are the backend-resolved employees (name + id) consumed by AuditInfo.
    createdAt?: string;
    createdBy?: string | null;
    lastModifiedAt?: string | null;
    lastModifiedBy?: string | null;
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
    /** A Rankings referee effort's lines, for a user with the feature. */
    nominations?: NominationLine[] | null;
    /** The people holding a nomination on it, for a user without the feature: they and the type are locked. */
    lockedRefereePartyIds?: number[] | null;
}

export interface EffortDto {
    id: string | number | null;
    effortActivityTypeCode?: string | null;
    description: string;
    effortDate: string | null;
    usedTimeInSeconds: number | null;
    effortByEmployee: { id: number } | null;
    employees: { id: number }[];
    matters: { id: number }[];
    partyPersons: { id: number }[];
    partyCompanies: { id: number }[];
    expenses: ExpenseDto[];
    /** Absent leaves the stored nominations as they are; an empty list removes them. */
    nominations?: NominationLine[];
}

export interface EffortForm {
    id: string | number | null;
    effortActivityType: { label: string; value: string } | null;
    description: string;
    effortDate: string | null;
    usedTimeInSeconds: number | null;
    usedTimeInHours: string | null;
    effortByEmployee: NamedEntityMinDto | null;
    employees: NamedEntityMinDto[];
    matters: {
        id: number;
        fullName: string;
        isConfidential?: boolean | null;
    }[];
    partyPersons: NamedEntityMinDto[];
    partyCompanies: NamedEntityMinDto[];
    expenses: ExpenseFormData[];
    /** Held only for a user with the Rankings referee feature; absent, a save leaves the stored ones alone. */
    nominations?: NominationLineForm[];
    /** Read only, never sent: the stored effort's `lockedRefereePartyIds`. */
    lockedRefereePartyIds?: number[];
}

export type NominationStatus = 'REQUESTED' | 'ACCEPTED' | 'DECLINED' | 'SUBMITTED';

export const NOMINATION_STATUSES: NominationStatus[] = ['REQUESTED', 'ACCEPTED', 'DECLINED', 'SUBMITTED'];

/** The activity type whose efforts carry nominations; every rule keys on this code, never on its label. */
export const RANKINGS_REFEREE_TYPE = 'RANKINGS_REFEREE';

/** One referee of a nomination line; `id` is the stored nomination's, absent for a new one. */
export interface NominationReferee {
    id?: number | null;
    party: NamedEntityMinDto;
    statusCode: NominationStatus;
}

/** One directory edition of a Rankings referee effort, with its referees. */
export interface NominationLine {
    legalDirectoryCode: string | null;
    editionYear: number | null;
    practiceArea: NamedEntityMinDto | null;
    referees: NominationReferee[];
}

/** A nomination line as the effort form edits it: the directory as its picker's option, the edition as typed. */
export interface NominationLineForm extends Omit<NominationLine, 'legalDirectoryCode' | 'editionYear'> {
    legalDirectory: { label: string; value: string } | null;
    editionYear: number | string | null;
}

/** One row of the Rankings referees list. */
export interface NominationListItem {
    id: number;
    effortId: number;
    referee: NamedEntityMinDto;
    /** The companies the referee represented, in name order: the effort's Effort for entities they work for. */
    companies: NamedEntityMinDto[];
    legalDirectoryCode: string;
    editionYear: number;
    practiceArea?: NamedEntityMinDto | null;
    requestedBy?: NamedEntityMinDto | null;
    requestedOn: string;
    statusCode: NominationStatus;
    canUpdateStatus: boolean;
}

export type NominationWarningType = 'ALREADY_NOMINATED' | 'RECENTLY_ASKED';

/** A warning on one referee of the form's line `lineIndex`, about another effort's nomination. */
export interface NominationWarning {
    lineIndex: number;
    partyId: number;
    type: NominationWarningType;
    legalDirectoryCode: string;
    editionYear: number;
    practiceArea?: NamedEntityMinDto | null;
    requestedBy?: NamedEntityMinDto | null;
    requestedOn: string;
}

export interface NominationWarningsRequest {
    effortId?: number | null;
    /** The effort date on the form, which the rest period counts back from; absent, today. */
    effortDate?: string | null;
    lines: { legalDirectoryCode: string | null; editionYear: number | null; partyIds: number[] }[];
}

interface EffortEmployee {
    employee: NamedEntityMinDto;
}

interface EffortParty {
    party: NamedEntityMinDto;
    effortPartyRoleTypeCode: string;
}

interface EffortMatter {
    matter: SimplifiedMatterDto;
}

interface SimplifiedMatterDto {
    id: number;
    name: string;
    caseNumber: string;
    fullName: string;
    isConfidential?: boolean | null;
}

export enum EffortPartyRoleType {
    FOR_PERSONS = 'FOR_PERSONS',
    FOR_ENTITIES = 'FOR_ENTITIES',
}

export interface GroupedEfforts {
    usedTimeInSeconds: number;
    efforts: EffortListItem[];
}
